import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Card } from "../ui/card";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { QuoteStatusSelect } from "./quotes/QuoteStatusSelect";

const STATUS_FILTERS = [
  { key: "todas", label: "Todas" },
  { key: "pendiente", label: "Pendiente" },
  { key: "contactado", label: "Contactado" },
  { key: "ganado", label: "Ganado" },
];

export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("todas");

  const load = useCallback(() => api.get("/admin/quotes").then((r) => setQuotes(r.data)), []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const updateStatus = async (quoteId, status) => {
    const previous = quotes;
    setQuotes((qs) => qs.map((q) => (q.id === quoteId ? { ...q, status } : q)));
    try {
      await api.put(`/admin/quotes/${quoteId}/status`, { status });
    } catch {
      toast.error("Error al actualizar el estado");
      setQuotes(previous);
    }
  };

  const filteredQuotes = statusFilter === "todas" ? quotes : quotes.filter((q) => (q.status || "pendiente") === statusFilter);

  if (loading) return <p className="text-muted-foreground">Cargando cotizaciones...</p>;

  return (
    <Card className="p-6 overflow-x-auto" data-testid="admin-quotes-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-semibold">
          Cotizaciones recibidas ({filteredQuotes.length}{statusFilter !== "todas" ? ` de ${quotes.length}` : ""})
        </h3>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList data-testid="admin-quotes-status-filter-tabs">
            {STATUS_FILTERS.map((f) => (
              <TabsTrigger key={f.key} value={f.key} data-testid={`admin-quotes-filter-${f.key}`}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {filteredQuotes.length === 0 ? (
        <p data-testid="admin-quotes-empty" className="text-muted-foreground text-sm">
          {statusFilter === "todas" ? "Aún no hay cotizaciones." : "No hay cotizaciones con este estado."}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Ciudad</TableHead>
              <TableHead>Paquetes</TableHead>
              <TableHead>Extras</TableHead>
              <TableHead>Precio Estimado</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredQuotes.map((q) => (
              <TableRow key={q.id} data-testid={`admin-quote-row-${q.id}`}>
                <TableCell>{q.name}</TableCell>
                <TableCell>{q.phone}</TableCell>
                <TableCell>{q.event_date}</TableCell>
                <TableCell>{q.city}</TableCell>
                <TableCell>{(q.packages || []).map((p) => p.name).join(", ")}</TableCell>
                <TableCell>{(q.extras || []).length ? q.extras.map((e) => e.label).join(", ") : "Ninguno"}</TableCell>
                <TableCell>${Number(q.estimated_price).toLocaleString("es-MX")}</TableCell>
                <TableCell>
                  <QuoteStatusSelect quoteId={q.id} status={q.status || "pendiente"} onChange={updateStatus} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};
